import { apiClient } from './client';

export type LeagueType = 'PAAR_LIGA' | 'MANNSCHAFTS_LIGA';

export interface League {
  id: number;
  name: string;
  league_type?: LeagueType;
  level?: string;
  status: 'draft' | 'open' | 'closed' | 'active' | 'knockout' | 'finished';
  registration_deadline?: string;
  end_date?: string;
  fee?: number;
  teams_per_group: number;
  max_players?: number;
  qualifiers_per_group: number;
  match_format?: string;
  description?: string;
  created_by?: string;
  activated_at?: string;
  finished_at?: string;
  created_at: string;
  // Paar-Liga
  pair_type?: string;
  // Mannschafts-Liga
  doubles_per_round?: number;
  rounds_per_encounter?: number;
  ranking_rule?: string;
  league_format?: string;
  min_players_per_team?: number;
  play_rounds?: number;
  player_count?: number;
  confirmed_count?: number;
  is_registered?: boolean;
  registration_id?: number;
  payment_status?: string;
}

export interface LeagueCreate {
  name: string;
  league_type?: LeagueType;
  level?: string;
  registration_deadline?: string;
  end_date?: string;
  fee?: number;
  teams_per_group?: number;
  max_players?: number;
  qualifiers_per_group?: number;
  match_format?: string;
  description?: string;
  pair_type?: string;
  doubles_per_round?: number;
  rounds_per_encounter?: number;
  ranking_rule?: string;
  league_format?: string;
  min_players_per_team?: number;
  play_rounds?: number;
}

export const leaguesApi = {
  list: (status?: string, league_type?: LeagueType) =>
    apiClient.get<League[]>('/api/leagues', {
      params: { ...(status ? { status } : {}), ...(league_type ? { league_type } : {}) },
    }),

  get: (id: number) => apiClient.get<League>(`/api/leagues/${id}`),

  create: (data: LeagueCreate) => apiClient.post<League>('/api/leagues', data),

  update: (id: number, data: Partial<LeagueCreate & { status: string }>) =>
    apiClient.put<League>(`/api/leagues/${id}`, data),

  register: (id: number, body?: { pair_id?: number; team_id?: number }) =>
    apiClient.post(`/api/leagues/${id}/register`, body ?? {}),

  withdraw: (id: number) => apiClient.delete(`/api/leagues/${id}/register`),

  getRegistrations: (id: number) => apiClient.get(`/api/leagues/${id}/registrations`),

  confirmPayment: (leagueId: number, regId: number) =>
    apiClient.post(`/api/leagues/${leagueId}/registrations/${regId}/confirm-payment`),

  removeRegistration: (leagueId: number, regId: number) =>
    apiClient.delete(`/api/leagues/${leagueId}/registrations/${regId}`),

  setPaymentStatus: (leagueId: number, regId: number, payment_status: string, waiver_note?: string) =>
    apiClient.patch(`/api/leagues/${leagueId}/registrations/${regId}/payment`, { payment_status, waiver_note }),

  activate: (id: number) => apiClient.post(`/api/leagues/${id}/activate`),

  startKnockout: (id: number) => apiClient.post(`/api/leagues/${id}/start-knockout`),

  finish: (id: number) => apiClient.post(`/api/leagues/${id}/finish`),

  getGroups: (id: number) => apiClient.get(`/api/leagues/${id}/groups`),

  getMatches: (id: number, params?: { phase?: string; group_id?: number }) =>
    apiClient.get(`/api/leagues/${id}/matches`, { params }),

  getEncounters: (id: number) => apiClient.get(`/api/leagues/${id}/encounters`),

  getStandings: (id: number) => apiClient.get(`/api/leagues/${id}/standings`),

  delete: (id: number) => apiClient.delete(`/api/leagues/${id}`),
};
